import { useState, useEffect, useCallback, useMemo } from 'react'
import { useWindowMode } from '../contexts/WindowModeProvider'

/**
 * Generate a unique window ID for this renderer instance
 */
function generateWindowId(): string {
  // Try to get window ID from URL hash or generate new one
  const hash = window.location.hash
  const match = hash.match(/windowId=([^&]+)/)
  if (match) {
    return match[1]
  }
  return `window-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Hook for managing window-specific UI state
 *
 * Window-specific states (like sidebar open/closed) stay local to each window.
 * This is different from global state which is shared across all windows.
 *
 * @example
 * ```tsx
 * const { uiState, updateUiState } = useWindowState()
 * const isSidebarOpen = uiState.sidebarLeft
 * ```
 */
export function useWindowState() {
  const windowId = useMemo(() => generateWindowId(), [])

  const [uiState, setUiState] = useState(() => {
    // Read from localStorage synchronously to avoid flash/animation on load
    try {
      const cached = localStorage.getItem('sidebar-state')
      if (cached) {
        const parsed = JSON.parse(cached)
        return {
          sidebarLeft: parsed.sidebarLeft ?? true,
          sidebarRight: parsed.sidebarRight ?? true
        }
      }
    } catch { /* ignore */ }
    return {
      sidebarLeft: true,
      sidebarRight: true
    }
  })

  useEffect(() => {
    if (!window.api) return

    // Get initial window state from Main Process
    const loadWindowState = async () => {
      try {
        // Load persisted sidebar state from settings
        const savedSidebar = (await window.api.invoke('get-sidebar-state')) as { sidebarLeft?: boolean; sidebarRight?: boolean } | null

        const state = (await window.api.invoke('window:get-state', windowId)) as { ui?: typeof uiState } | null
        if (state?.ui) {
          setUiState(state.ui)
        } else if (savedSidebar) {
          setUiState(prev => ({
            ...prev,
            sidebarLeft: savedSidebar.sidebarLeft ?? true,
            sidebarRight: savedSidebar.sidebarRight ?? true
          }))
        }
      } catch (error) {
        console.error('[useWindowState] Failed to load window state:', error)
      }
    }

    loadWindowState()

    // Listen for window state updates
    const handleWindowStateUpdate = (data: { windowId: string; state: any }) => {
      if (data.windowId === windowId && data.state?.ui) {
        setUiState(data.state.ui)
      }
    }

    window.api.receive('window-state-updated', handleWindowStateUpdate)

    // Cleanup
    return () => {
      window.api?.removeAllListeners('window-state-updated')
    }
  }, [windowId])

  /**
   * Update window-specific UI state
   */
  const updateUiState = useCallback((updates: Partial<typeof uiState>) => {
    const newState = { ...uiState, ...updates }
    setUiState(newState)

    // Notify Main Process (for info only, not for sync)
    window.api?.send('window:ui-state', windowId, newState)

    // Persist sidebar state to settings + localStorage cache
    if ('sidebarLeft' in updates || 'sidebarRight' in updates) {
      const sidebarState = {
        sidebarLeft: newState.sidebarLeft,
        sidebarRight: newState.sidebarRight
      }
      localStorage.setItem('sidebar-state', JSON.stringify(sidebarState))
      window.api?.invoke('save-sidebar-state', sidebarState)
    }
  }, [windowId, uiState])

  /**
   * Toggle sidebar visibility
   */
  const toggleSidebar = useCallback((side: 'left' | 'right') => {
    updateUiState({
      [`sidebar${side.charAt(0).toUpperCase() + side.slice(1)}`]: !uiState[`sidebar${side.charAt(0).toUpperCase() + side.slice(1)}`]
    })
  }, [uiState, updateUiState])

  return {
    windowId,
    uiState,
    updateUiState,
    toggleSidebar
  }
}

/**
 * Hook for window mode management (full ↔ list ↔ stack).
 *
 * Thin wrapper around {@link useWindowMode} (WindowModeProvider) so the window
 * mode is a single shared source of truth, read synchronously across the app.
 */
export function useViewMode() {
  const {
    viewMode, isListMode, isStackMode,
    switchToListMode, switchToStackMode, switchToFullMode,
    toggleListMode, toggleStackMode,
  } = useWindowMode()
  return {
    viewMode,
    isFullMode: viewMode === 'full',
    isListMode,
    isStackMode,
    switchToListMode,
    switchToStackMode,
    switchToFullMode,
    toggleListMode,
    toggleStackMode,
  }
}